import type { CaptureNode } from "@capture/captureClient";
import { useSetRoomAudioGate } from "@capture/captureQueries";
import type { AudioGateMode } from "@rooms/roomsClient";
import {
  FieldError,
  Input,
  SegmentedControl,
  type SegmentedControlOption,
  StatusMark,
  Swipe,
} from "@scribenote/brian-design-system";
import { useId, useRef, useState } from "react";

import {
  describeGateReading,
  type RoomAudioGateState,
  roomAudioGateState,
} from "./roomAudioGateState";

/** Stands in for the wire's null; SegmentedControl values are strings. */
const DEFAULT_SEGMENT = "default";
type GateSegment = "off" | "adaptive" | typeof DEFAULT_SEGMENT;

/** `fixed` is an engineer's escape hatch, deliberately not offered; it shows as Default. */
const SEGMENTS: readonly { value: GateSegment; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "adaptive", label: "Adaptive" },
  { value: DEFAULT_SEGMENT, label: "Default" },
];

const toSegment = (mode: AudioGateMode): GateSegment =>
  mode === "off" || mode === "adaptive" ? mode : DEFAULT_SEGMENT;

const toMode = (segment: GateSegment): AudioGateMode =>
  segment === DEFAULT_SEGMENT ? null : segment;

/** Mirrors Brian's `RoomAudioGateSet.margin_db` bounds and Zuul's env default. */
const MARGIN_MIN_DB = 1;
const MARGIN_MAX_DB = 40;
const DEFAULT_MARGIN_DB = 12;

const STATE_WORD: Record<string, string> = {
  bypass: "relaying everything",
  fixed: "fixed threshold",
  adaptive_warming: "learning the room",
  adaptive_ready: "gating on the learned floor",
};

interface RoomAudioGateProps {
  roomUuid: string;
  roomName: string;
  /** `undefined` while the roster has not loaded. */
  nodes: CaptureNode[] | undefined;
  desiredMode: AudioGateMode;
  desiredMarginDb: number | null;
  desiredRevision: number;
}

export const RoomAudioGateControl = ({
  roomUuid,
  roomName,
  nodes,
  desiredMode,
  desiredMarginDb,
  desiredRevision,
}: RoomAudioGateProps) => {
  const mutation = useSetRoomAudioGate();
  const statusId = useId();
  const marginErrorId = useId();
  const [marginDraft, setMarginDraft] = useState<string | null>(null);
  const [marginError, setMarginError] = useState<string | null>(null);
  /* The settle callback has to read the segment queued after it was created. */
  const queuedSegment = useRef<GateSegment | null>(null);
  const [queued, setQueued] = useState(false);
  const gate = roomAudioGateState(
    nodes,
    {
      mode: desiredMode,
      marginDb: desiredMarginDb,
      revision: desiredRevision,
    },
    roomUuid,
    mutation.data,
  );
  const busy =
    mutation.isPending ||
    queued ||
    (gate.pending && !gate.unavailable && !gate.unreported);
  const options: readonly SegmentedControlOption<GateSegment>[] = SEGMENTS;

  /*
   * A mid-PUT segment click is queued, not dropped: blurring the margin input
   * sends its PUT before the click lands. One slot (last intent wins), one
   * request at a time so PUTs can't reorder; a failed PUT drops the queue.
   */
  const submitGate = (mode: AudioGateMode, marginDb: number | null): void => {
    mutation.mutate(
      { roomUuid, mode, marginDb },
      {
        onSettled: (applied) => {
          const next = queuedSegment.current;
          if (next === null) return;
          queuedSegment.current = null;
          setQueued(false);
          if (applied === undefined) return;
          submitGate(toMode(next), applied.marginDb);
        },
      },
    );
  };

  const status = gate.failed
    ? { word: "Gate control failed", className: "text-ink-stopped" }
    : gate.unreported
      ? { word: "Not reported by this Zuul", className: "text-annotation" }
      : gate.unavailable && gate.revision > 0
        ? { word: "Applies on reconnect", className: "text-annotation" }
        : busy
          ? { word: "Applying", className: "text-annotation" }
          : null;

  const commitMargin = () => {
    if (marginDraft === null) return;
    const trimmed = marginDraft.trim();
    const next = trimmed === "" ? null : Number(trimmed);
    if (
      next !== null &&
      (!Number.isFinite(next) || next < MARGIN_MIN_DB || next > MARGIN_MAX_DB)
    ) {
      setMarginError(
        `Margin must be between ${MARGIN_MIN_DB} and ${MARGIN_MAX_DB} dB.`,
      );
      return;
    }
    setMarginError(null);
    setMarginDraft(null);
    if (next !== gate.marginDb) {
      submitGate(gate.mode, next);
    }
  };

  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <SegmentedControl<GateSegment>
          aria-label={`Audio gate for ${roomName}`}
          aria-describedby={status ? statusId : undefined}
          size="sm"
          options={options}
          value={toSegment(gate.mode)}
          onValueChange={(segment) => {
            if (mutation.isPending) {
              queuedSegment.current = segment;
              setQueued(true);
              return;
            }
            submitGate(toMode(segment), gate.marginDb);
          }}
          aria-busy={busy || undefined}
          data-testid={`room-audio-gate-${roomUuid}`}
        />
        <span className="inline-flex items-center gap-1">
          <span className="w-16">
            <Input
              aria-label={`Audio gate margin for ${roomName}, in decibels above the floor`}
              aria-describedby={marginError ? marginErrorId : undefined}
              type="number"
              inputMode="decimal"
              min={MARGIN_MIN_DB}
              max={MARGIN_MAX_DB}
              step={1}
              placeholder={String(DEFAULT_MARGIN_DB)}
              value={
                marginDraft ?? (gate.marginDb === null ? "" : gate.marginDb)
              }
              disabled={gate.mode === "off" || mutation.isPending}
              onChange={(event) => setMarginDraft(event.target.value)}
              onBlur={commitMargin}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
                if (event.key === "Escape") {
                  setMarginDraft(null);
                  setMarginError(null);
                }
              }}
              className="px-2 py-1 font-mono text-xs tabular-nums"
              data-testid={`room-audio-gate-margin-${roomUuid}`}
            />
          </span>
          <span className="text-xs text-annotation" aria-hidden="true">
            dB
          </span>
        </span>
        {status && (
          <span id={statusId} className={`text-xs ${status.className}`}>
            {status.word}
          </span>
        )}
      </span>
      {marginError && <FieldError id={marginErrorId}>{marginError}</FieldError>}
      {mutation.isError && (
        <FieldError>
          Couldn&rsquo;t update the audio gate. Try again.
        </FieldError>
      )}
    </span>
  );
};

export const RoomAudioGateReadout = ({
  roomUuid,
  nodes,
}: Pick<RoomAudioGateProps, "roomUuid" | "nodes">) => {
  const gate: RoomAudioGateState = roomAudioGateState(nodes, {}, roomUuid);
  const testId = `room-audio-gate-reading-${roomUuid}`;
  if (nodes !== undefined && nodes.length === 0) {
    return <StatusMark data-testid={testId}>no node</StatusMark>;
  }
  if (gate.unreported) {
    return (
      <StatusMark data-testid={testId}>Not reported by this Zuul</StatusMark>
    );
  }
  const { reading: measured } = gate;
  const stateWord =
    measured.state === null
      ? null
      : (STATE_WORD[measured.state] ?? measured.state);

  return (
    <span
      className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-annotation"
      title="Level is the loudest second since Zuul's previous roster snapshot. Refreshes when the gate changes or the level moves by a 6 dB step over the floor, and at least every 5 minutes; re-read every 30 seconds."
      data-testid={testId}
    >
      {measured.open === null ? (
        <StatusMark>Gate unknown</StatusMark>
      ) : measured.open ? (
        <Swipe tone="working">Streaming</Swipe>
      ) : (
        <StatusMark>Gate closed</StatusMark>
      )}
      {stateWord && <span>{stateWord}</span>}
      <span className="font-mono text-fine-print tabular-nums">
        {describeGateReading(measured)}
      </span>
    </span>
  );
};
